import React, { useState, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import ExportJsonExcel from "js-export-excel";

// Apartados de inventario: reservan piezas de una variante (modelo + color +
// talla) en un almacen. Mientras estan Activos se descuentan del disponible;
// al liberarlos vuelven a estar disponibles. No mueven el inventario.
const URL_APARTADOS = process.env.REACT_APP_URL_APARTADOS;
const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;
const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
const URL_AREAS = process.env.REACT_APP_URL_AREAS;
const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const nombreColor = (c) => (c ? (c.name) : "");

function ListadoApartados() {
  const { user } = useContext(AuthContext);
  const [apartados, setApartados] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [status, setStatus] = useState("Activo");
  const [busqueda, setBusqueda] = useState("");

  // alta
  const [modal, setModal] = useState(false);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [articulo, setArticulo] = useState(null);
  const [area, setArea] = useState(process.env.REACT_APP_AREAS_CEDIS || "");
  const [cliente, setCliente] = useState("");
  const [fechaVence, setFechaVence] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [variantes, setVariantes] = useState([]); // existencias del modelo en el almacen
  const [cantidades, setCantidades] = useState({}); // idInv -> cantidad a apartar
  const [guardando, setGuardando] = useState(false);

  function carga() {
    axios.get(URL_APARTADOS, { headers: headers() })
      .then((r) => setApartados(r.data || []))
      .catch((e) => console.log(e));
  }
  useEffect(() => {
    carga();
    axios.get(`${URL_ARTICULOS}Activos`, { headers: headers() }).then((r) => setArticulos(r.data || [])).catch(() => {});
    axios.get(URL_AREAS, { headers: headers() }).then((r) => setAreas((r.data || []).filter((a) => a.is_active !== "No"))).catch(() => {});
    axios.get(URL_CLIENTES, { headers: headers() }).then((r) => setClientes((r.data || []).filter((c) => c.is_active !== "No"))).catch(() => {});
  }, []);

  // Al elegir modelo + almacen se traen sus variantes con existencia y lo ya comprometido
  useEffect(() => {
    setVariantes([]); setCantidades({});
    if (!articulo || !area) return;
    axios.get(`${URL_INVENTARIOS}VariantesAlmacen/${area}/${articulo._id}`, { headers: headers() })
      .then((r) => setVariantes((r.data || []).filter((v) => v.inventario > 0)))
      .catch((e) => console.log(e));
  }, [articulo, area]);

  const filas = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return apartados
      .filter((a) => !status || a.status === status)
      .filter((a) => !t || [
        a.idApartado, a.articulos?.[0]?.codigo, a.articulos?.[0]?.nombre, a.clientes?.[0]?.nombre_comercial,
        nombreColor(a.colores?.[0]), a.tallas?.[0]?.name, a.observaciones,
      ].some((v) => String(v || "").toLowerCase().includes(t)))
      .sort((x, y) => (x.idApartado > y.idApartado ? -1 : 1));
  }, [apartados, status, busqueda]);

  const totalPiezas = filas.reduce((s, a) => s + (Number(a.cantidad) || 0), 0);

  async function guardar() {
    const lineas = variantes.filter((v) => Number(cantidades[v.idInv]) > 0);
    if (!lineas.length) {
      Swal.fire({ icon: "warning", title: "Sin cantidades", text: "Captura cuantas piezas apartar en al menos una variante." });
      return;
    }
    const excedida = lineas.find((v) => Number(cantidades[v.idInv]) > v.disponible);
    if (excedida) {
      Swal.fire({ icon: "error", title: "No alcanza", text: `${excedida.color} / ${excedida.talla}: solo hay ${excedida.disponible} libres.` });
      return;
    }
    setGuardando(true);
    try {
      for (const v of lineas) {
        await axios.post(URL_APARTADOS, {
          fecha, articulos: articulo._id, colores: v.idColor, tallas: v.idTalla, areas: area,
          clientes: cliente || "", user: user.id, cantidad: Number(cantidades[v.idInv]),
          fechaVence, observaciones: observaciones || "NA",
        }, { headers: headers() });
      }
      setGuardando(false); setModal(false);
      setArticulo(null); setCliente(""); setObservaciones(""); setFechaVence("");
      carga();
      Swal.fire({ icon: "success", title: "Apartado", text: `${lineas.length} variante(s) apartada(s)`, timer: 1500, showConfirmButton: false });
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo apartar", text: `${error.response?.data?.message || error.response?.data || error.message}` });
    }
  }

  async function liberar(a) {
    const ok = await Swal.fire({
      icon: "question", title: `¿Liberar el apartado ${a.idApartado}?`,
      text: `${num(a.cantidad)} pzas de ${a.articulos?.[0]?.codigo} vuelven al disponible.`,
      showCancelButton: true, confirmButtonText: "Sí, liberar", cancelButtonText: "No",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.patch(`${URL_APARTADOS}/liberar/${a._id}`, {}, { headers: headers() });
      carga();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo liberar", text: `${error.response?.data?.message || error.message}` });
    }
  }

  function excel() {
    const data = filas.map((a) => ({
      Folio: a.idApartado, Fecha: a.fecha, Status: a.status, Almacen: a.areas?.[0]?.name || "",
      Modelo: `${a.articulos?.[0]?.codigo || ""} ${a.articulos?.[0]?.nombre || ""}`.trim(),
      Color: nombreColor(a.colores?.[0]), Talla: a.tallas?.[0]?.name || "", Cantidad: a.cantidad,
      Cliente: a.clientes?.[0]?.nombre_comercial || "", Vence: a.fechaVence || "", Observaciones: a.observaciones || "",
      Usuario: a.user?.[0] ? `${a.user?.[0]?.nombre} ${a.user?.[0]?.apellido}` : "",
    }));
    const cols = Object.keys(data[0] || { Folio: 1 });
    new ExportJsonExcel({ fileName: "Apartados", datas: [{ sheetData: data, sheetName: "Apartados", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  }

  const opcionesArt = useMemo(() => articulos.slice().sort((a, b) => (a.codigo > b.codigo ? 1 : -1)), [articulos]);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.inventarios ? (
        <div className="card container col-12">
          <Row className="mb-2">
            <Col md={4}>
              <Button href="/MenuInventarios" className="btn btn-danger" size="sm">Regresar</Button>{" "}
              <Button color="success" size="sm" onClick={() => setModal(true)}>Nuevo apartado</Button>{" "}
              <Button className="btn btn-primary" size="sm" onClick={excel}>Excel</Button>
            </Col>
            <Col md={2}>
              <Label>Status</Label>
              <Input bsSize="sm" type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="Activo">Activos</option>
                <option value="Liberado">Liberados</option>
              </Input>
            </Col>
            <Col md={4}>
              <Label>Buscar</Label>
              <Input bsSize="sm" type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Folio, modelo, cliente, color, talla" />
            </Col>
            <Col md={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>{filas.length} apartado(s) · {num(totalPiezas)} pzas</p>
            </Col>
          </Row>

          <h3 align="center">Apartados de inventario</h3>
          <p align="center" style={{ color: "#64748b" }}>Reservan piezas por modelo, color y talla; mientras estén activos se descuentan del disponible.</p>
          <Table size="sm" striped bordered responsive>
            <thead>
              <tr>
                <th>Folio</th><th>Fecha</th><th>Status</th><th>Almacén</th><th>Modelo</th><th>Color</th><th>Talla</th>
                <th className="text-right">Piezas</th><th>Cliente</th><th>Vence</th><th>Observaciones</th><th>Usuario</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((a) => (
                <tr key={a._id}>
                  <td>{a.idApartado}</td>
                  <td>{a.fecha}</td>
                  <td>
                    <span style={{ fontWeight: 700, color: a.status === "Activo" ? "#15803d" : "#6b7280" }}>{a.status}</span>
                  </td>
                  <td>{a.areas?.[0]?.name}</td>
                  <td>{a.articulos?.[0]?.codigo} {a.articulos?.[0]?.nombre}</td>
                  <td>{nombreColor(a.colores?.[0])}</td>
                  <td>{a.tallas?.[0]?.name}</td>
                  <td className="text-right">{num(a.cantidad)}</td>
                  <td>{a.clientes?.[0]?.nombre_comercial}</td>
                  <td>{a.fechaVence}</td>
                  <td>{a.observaciones}</td>
                  <td>{a.user?.[0] ? `${a.user?.[0]?.nombre} ${a.user?.[0]?.apellido}` : ""}</td>
                  <td>
                    {a.status === "Activo" ? (
                      <Button size="sm" color="warning" onClick={() => liberar(a)} title="Liberar: vuelve al disponible">
                        <i className="fas fa-unlock" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {filas.length === 0 ? <tr><td colSpan={13} align="center" style={{ color: "#9ca3af" }}>Sin apartados</td></tr> : null}
            </tbody>
          </Table>
        </div>
      ) : undefined}

      <Modal size="lg" isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>Nuevo apartado</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={2}>
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>Almacén</Label>
              <Input type="select" value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">Selecciona</option>
                {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              </Input>
            </Col>
            <Col md={7}>
              <Label>Modelo</Label>
              <Autocomplete
                size="small"
                options={opcionesArt}
                getOptionLabel={(o) => `${o.codigo || ""} ${o.nombre || ""}`.trim()}
                value={articulo}
                onChange={(e, v) => setArticulo(v)}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca por código o nombre" />}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={4}>
              <Label>Cliente (opcional)</Label>
              <Input type="select" value={cliente} onChange={(e) => setCliente(e.target.value)}>
                <option value="">Sin cliente</option>
                {clientes.slice().sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((c) => (
                  <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Vence (opcional)</Label>
              <Input type="date" value={fechaVence} onChange={(e) => setFechaVence(e.target.value)} />
            </Col>
            <Col md={5}>
              <Label>Observaciones</Label>
              <Input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
            </Col>
          </Row>
          <br />
          {articulo && area ? (
            variantes.length ? (
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Color</th><th>Talla</th>
                    <th className="text-right">Existencia</th><th className="text-right">Comprometido</th>
                    <th className="text-right">Libre</th><th style={{ width: 130 }}>Apartar</th>
                  </tr>
                </thead>
                <tbody>
                  {variantes.map((v) => (
                    <tr key={v.idInv}>
                      <td>{v.color}</td>
                      <td>{v.talla}</td>
                      <td className="text-right">{num(v.inventario)}</td>
                      <td className="text-right">{num(v.comprometido)}</td>
                      <td className="text-right"><b>{num(v.disponible)}</b></td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" max={v.disponible} value={cantidades[v.idInv] || ""}
                          onChange={(e) => setCantidades({ ...cantidades, [v.idInv]: e.target.value })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : <p style={{ color: "#9ca3af" }}>Ese modelo no tiene existencia en el almacén elegido.</p>
          ) : <p style={{ color: "#9ca3af" }}>Elige almacén y modelo para ver sus variantes.</p>}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={guardar} disabled={guardando}>{guardando ? "Guardando..." : "Apartar"}</Button>
          <Button color="secondary" onClick={() => setModal(false)}>Cancelar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ListadoApartados;
